import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseFloat(this.refs.cartBubbleCount.textContent ?? '0');
  }

  set currentCartCount(value) {
    const displayQty = parseFloat(value).toFixed(1).replace(/\.0$/, '');
    this.refs.cartBubbleCount.textContent = value < 100 ? displayQty : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
    this.observeCartBubbleChanges();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this._bubbleObserver?.disconnect();
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event
   */
  onCartUpdate = async (event) => {
    // Ignore this event and instead fetch fresh cart to apply our decimal logic
    await this.fetchAndUpdateCartCount(true);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - fallback count
   * @param {boolean} comingFromProductForm
   * @param {boolean} animate
   */
  renderCartBubble = async (itemCount = 0, comingFromProductForm = false, animate = true) => {
    const bubble = this.refs.cartBubble;
    const count = this.currentCartCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    bubble.classList.toggle('visually-hidden', count === 0);
    bubble.classList.toggle('cart-bubble--animating', count > 0 && animate);

    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    bubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      if (Date.now() - timestamp < 10000) {
        const count = parseFloat(value);
        if (count >= 0) {
          this.currentCartCount = count;
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // do nothing
    }
  };

  observeCartBubbleChanges() {
    this._bubbleObserver = new MutationObserver(() => {
      this.fetchAndUpdateCartCount(false);
    });

    this._bubbleObserver.observe(this.refs.cartBubbleCount, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  async fetchAndUpdateCartCount(animate = true) {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      let totalQty = 0;
      cart.items.forEach((item) => {
        const isPerMeter = item.properties && item.properties._pm === '1';
        totalQty += isPerMeter ? item.quantity * 0.5 : item.quantity;
      });

      this.currentCartCount = totalQty;
      this.renderCartBubble(totalQty, false, animate);
    } catch (e) {
      console.error('Cart fetch failed:', e);
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
